"use client";

import { useTranslations } from "next-intl";
import Link from "next/link";
import { useCallback, useMemo, useState } from "react";
import { CheckCircle2, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  isCollectorPreferenceKey,
  isCollectorRoleKey,
  type CollectorPreferenceKey,
  type CollectorRoleKey,
} from "@/lib/collector-preference-keys";

const CATEGORY_META: { id: CollectorPreferenceKey; emoji: string }[] = [
  { id: "lego", emoji: "\u{1F9F1}" },
  { id: "sneakers", emoji: "\u{1F45F}" },
  { id: "trading_cards", emoji: "\u{1F0CF}" },
  { id: "figures", emoji: "\u{1F5FF}" },
  { id: "vintage", emoji: "\u{1F4FC}" },
  { id: "watches", emoji: "\u{231A}" },
  { id: "comics", emoji: "\u{1F4DA}" },
  { id: "art", emoji: "\u{1F3A8}" },
];

const ROLE_META: { id: CollectorRoleKey; emoji: string }[] = [
  { id: "buyer", emoji: "\u{1F6CD}\u{FE0F}" },
  { id: "seller", emoji: "\u{1F4E6}" },
  { id: "both", emoji: "\u{1F504}" },
];

export type OnboardingModalVariant = "blocking" | "preferencesEdit";

function initialCategorySet(
  variant: OnboardingModalVariant,
  userCreatedAt: string | null,
  hydrate?: { categories: string[]; role: string | null } | null
): Set<CollectorPreferenceKey> {
  if (hydrate?.categories?.length) {
    const s = new Set<CollectorPreferenceKey>();
    for (const c of hydrate.categories) {
      if (isCollectorPreferenceKey(c)) s.add(c);
    }
    if (s.size) return s;
  }
  if (variant === "preferencesEdit") return new Set();
  const fresh = userCreatedAt ? Date.now() - new Date(userCreatedAt).getTime() < 30 * 60 * 1000 : false;
  if (fresh) return new Set();
  return new Set<CollectorPreferenceKey>(["lego"]);
}

export function OnboardingModal({
  variant,
  userCreatedAt,
  hydrate,
  onDoneAction,
}: {
  variant: OnboardingModalVariant;
  userCreatedAt: string | null;
  hydrate?: { categories: string[]; role: string | null } | null;
  onDoneAction: (payload: { kind: "blocking-complete" | "preferences-saved" }) => void;
}) {
  const t = useTranslations("onboarding");
  const tCat = useTranslations("categories");
  const tRoles = useTranslations("roles");
  const [step, setStep] = useState<1 | 2 | 3 | 4>(() => (variant === "preferencesEdit" ? 2 : 1));
  const [categories, setCategories] = useState<Set<CollectorPreferenceKey>>(() =>
    initialCategorySet(variant, userCreatedAt, hydrate ?? null)
  );
  const [role, setRole] = useState<CollectorRoleKey | null>(() => {
    const r = hydrate?.role;
    if (r && isCollectorRoleKey(r)) return r;
    return null;
  });
  const [saving, setSaving] = useState(false);

  const progress = useMemo(() => (step === 2 ? 0.5 : step >= 3 ? 1 : 0), [step]);

  const toggleCategory = useCallback((id: CollectorPreferenceKey) => {
    setCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const submitPreferences = useCallback(async () => {
    if (!role) return;
    setSaving(true);
    try {
      const res = await fetch("/api/user/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          categories: Array.from(categories),
          role,
          onboardingComplete: variant === "blocking",
        }),
      });
      if (!res.ok) throw new Error("save failed");
      if (variant === "preferencesEdit") {
        onDoneAction({ kind: "preferences-saved" });
      } else {
        setStep(4);
      }
    } finally {
      setSaving(false);
    }
  }, [categories, onDoneAction, role, variant]);

  const showProgress = step >= 2 && step <= 3;
  const showFloatingCta =
    step === 1 || step === 2 || step === 3 || (step === 4 && variant === "blocking");

  const floatingFooterPad = { paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" } as const;
  const scrollBottomPad = showFloatingCta
    ? ({ paddingBottom: "calc(7.25rem + env(safe-area-inset-bottom))" } as const)
    : undefined;

  return (
    <div
      className="fixed inset-0 z-[30000] flex h-dvh max-h-dvh flex-col bg-background text-foreground"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      {showProgress ? (
        <div className="mx-auto w-full max-w-md shrink-0 px-6 pt-[max(1.25rem,env(safe-area-inset-top))] pb-5">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {step === 2 ? t("steps.oneOfTwo") : t("steps.twoOfTwo")}
          </p>
        </div>
      ) : null}

      {step === 1 ? (
        <div className="relative z-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div
            className="absolute inset-0 overflow-y-auto overscroll-contain px-6 scrollbar-none [&::-webkit-scrollbar]:hidden"
            style={{
              ...scrollBottomPad,
              paddingTop: "max(1.25rem, env(safe-area-inset-top))",
            }}
          >
            <div className="mx-auto flex min-h-full w-full max-w-md flex-col">
              <div className="h-16 shrink-0 md:h-24" aria-hidden />
              <div className="flex flex-1 flex-col items-center justify-center py-10 text-center md:py-16">
                <div className="mb-10 text-3xl font-semibold tracking-tight">Kraahl</div>
                <div
                  className="mb-8 flex h-28 w-28 items-center justify-center rounded-full bg-green-500/15 text-green-600 dark:text-green-400"
                  aria-hidden
                >
                  <Sparkles className="h-14 w-14" strokeWidth={1.35} />
                </div>
                <h1 id="onboarding-title" className="mb-4 text-2xl font-semibold tracking-tight">
                  {t("hook.heading")}
                </h1>
                <p className="text-base leading-relaxed text-muted-foreground">{t("hook.body")}</p>
              </div>
              <div className="h-16 shrink-0 md:h-24" aria-hidden />
            </div>
          </div>
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-28 bg-linear-to-t from-background to-transparent"
            aria-hidden
          />
          <div
            className="absolute bottom-0 left-0 right-0 z-20 px-6 pt-5"
            style={floatingFooterPad}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="mx-auto w-full max-w-md">
              <Button
                type="button"
                className="group h-14 w-full rounded-full text-sm shadow-md"
                onClick={() => setStep(2)}
              >
                {t("hook.cta")}
                <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {step === 2 || step === 3 || (step === 4 && variant === "blocking") ? (
        <div className="relative z-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div
            className="absolute inset-0 overflow-y-auto overscroll-contain px-6 scrollbar-none [&::-webkit-scrollbar]:hidden"
            style={{
              ...scrollBottomPad,
              paddingTop: showProgress ? 0 : "max(1.25rem, env(safe-area-inset-top))",
            }}
          >
            <div className="mx-auto flex min-h-full w-full max-w-md flex-col">
              <div className="h-16 shrink-0 md:h-24" aria-hidden />
              <div className="flex flex-1 flex-col justify-center py-8 md:py-14">
                {step === 2 ? (
                <>
                  <h1 className="mb-2 text-center text-2xl font-semibold tracking-tight">{t("categories.heading")}</h1>
                  <p className="mb-8 text-center text-sm text-muted-foreground">{t("categories.subtitle")}</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {CATEGORY_META.map(({ id, emoji }) => {
                      const selected = categories.has(id);
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggleCategory(id)}
                          className={cn(
                            "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                            selected
                              ? "border-primary bg-primary/10 text-foreground"
                              : "border-border bg-muted/40 text-muted-foreground hover:bg-muted/70"
                          )}
                        >
                          <span className="text-lg" aria-hidden>
                            {emoji}
                          </span>
                          {tCat(id)}
                        </button>
                      );
                    })}
                  </div>
                </>
                ) : null}

                {step === 3 ? (
                <>
                  <h1 className="mb-2 text-center text-2xl font-semibold tracking-tight">{t("role.heading")}</h1>
                  <p className="mb-8 text-center text-sm text-muted-foreground">{t("role.subtitle")}</p>
                  <div className="flex w-full flex-col gap-3">
                    {ROLE_META.map(({ id, emoji }) => {
                      const selected = role === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setRole(id)}
                          className={cn(
                            "flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition-colors",
                            selected
                              ? "border-primary bg-primary/10"
                              : "border-border bg-card hover:bg-muted/50"
                          )}
                        >
                          <span className="text-2xl" aria-hidden>
                            {emoji}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold">{tRoles(id)}</span>
                            <span className="mt-1 block text-sm text-muted-foreground">
                              {id === "buyer"
                                ? t("role.buyerSub")
                                : id === "seller"
                                  ? t("role.sellerSub")
                                  : t("role.bothSub")}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
                ) : null}

                {step === 4 && variant === "blocking" ? (
                <div className="flex flex-col items-center text-center">
                  <div className="mb-8 text-green-600 dark:text-green-400" aria-hidden>
                    <CheckCircle2 className="h-24 w-24" strokeWidth={1.25} />
                  </div>
                  <h1 className="mb-4 text-2xl font-semibold tracking-tight">{t("confirm.heading")}</h1>
                  <p className="mb-4 text-base leading-relaxed text-muted-foreground">{t("confirm.body")}</p>
                  <p className="text-sm text-muted-foreground">
                    <Link href="/settings" className="font-medium text-primary underline underline-offset-2">
                      {t("confirm.profileLink")}
                    </Link>
                  </p>
                </div>
                ) : null}
              </div>
              <div className="h-16 shrink-0 md:h-24" aria-hidden />
            </div>
          </div>

          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-28 bg-linear-to-t from-background to-transparent"
            aria-hidden
          />
          <div
            className="absolute bottom-0 left-0 right-0 z-20 px-6 pt-5"
            style={floatingFooterPad}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="mx-auto w-full max-w-md">
              {step === 2 ? (
                <Button type="button" className="group h-14 w-full rounded-full text-sm shadow-md" onClick={() => setStep(3)}>
                  {t("categories.cta")}
                  <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              ) : null}
              {step === 3 ? (
                <Button
                  type="button"
                  className="group h-14 w-full rounded-full text-sm shadow-md"
                  disabled={!role || saving}
                  onClick={() => void submitPreferences()}
                >
                  {t("role.cta")}
                  <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              ) : null}
              {step === 4 && variant === "blocking" ? (
                <Button
                  type="button"
                  className="group h-14 w-full rounded-full text-sm shadow-md"
                  onClick={() => onDoneAction({ kind: "blocking-complete" })}
                >
                  {t("confirm.cta")}
                  <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
